import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Toggle from 'src/components/ui/Toggle/Toggle';
import Modal from 'src/components/layouts/Modal/Modal';
import { useMcpKeyMutations, useMcpKeyStatusQuery } from 'src/services/mcpApiKey';

interface McpApiKeySettingsProps {
  hasWriteAccess?: boolean;
}

const McpApiKeySettings: React.FC<McpApiKeySettingsProps> = ({ hasWriteAccess = false }) => {
  const { t } = useTranslation();
  const { data: status, isLoading } = useMcpKeyStatusQuery();
  const { generateMutation, revokeMutation } = useMcpKeyMutations();

  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  // Holds the raw key only between generation and closing the reveal modal.
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);

  const hasKey = !!status?.has_key;
  const busy = generateMutation.isPending || revokeMutation.isPending;

  const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '—');

  const handleGenerate = async () => {
    setShowRegenerateConfirm(false);
    try {
      const result = await generateMutation.mutateAsync({});
      setGeneratedKey(result.api_key);
    } catch {
      toast.error(t('settings.mcp_api.generate_error'));
    }
  };

  const handleRevoke = async () => {
    setShowRevokeConfirm(false);
    try {
      await revokeMutation.mutateAsync({});
      toast.success(t('settings.mcp_api.revoke_success'));
    } catch {
      toast.error(t('settings.mcp_api.revoke_error'));
    }
  };

  const handleCopy = async () => {
    if (!generatedKey) return;
    try {
      await navigator.clipboard.writeText(generatedKey);
      toast.success(t('settings.mcp_api.copied'));
    } catch {
      toast.error(t('settings.mcp_api.copy_error'));
    }
  };

  return (
    <div className="py-24">
      <div className="rounded-base surface-elevated border border-subtle card-shadow p-24 max-w-720">
        <h2 className="typo-title-20 text-full mb-8">{t('settings.mcp_api.title')}</h2>
        <p className="typo-text-14 text-faded mb-24">{t('settings.mcp_api.description')}</p>

        <div className="flex items-center gap-12 mb-20">
          <Toggle checked={hasKey} readOnly />
          <span className="typo-text-14 text-faded">
            {hasKey ? t('settings.mcp_api.status_active') : t('settings.mcp_api.status_inactive')}
          </span>
        </div>

        {hasKey && (
          <div className="flex flex-col gap-4 mb-24">
            <div className="flex gap-8 typo-text-14">
              <span className="text-faded">{t('settings.mcp_api.generated_on')}:</span>
              <span className="text-full">{formatDate(status?.generated_on)}</span>
            </div>
            <div className="flex gap-8 typo-text-14">
              <span className="text-faded">{t('settings.mcp_api.last_used')}:</span>
              <span className="text-full">{formatDate(status?.last_used)}</span>
            </div>
          </div>
        )}

        {!isLoading && (
          <div className="flex gap-12">
            {!hasKey && (
              <Button
                tone={ButtonToneProps.PRIMARY_BLUE}
                title={t('settings.mcp_api.generate')}
                disabled={!hasWriteAccess || busy}
                onClick={handleGenerate}
              />
            )}
            {hasKey && (
              <>
                <Button
                  tone={ButtonToneProps.PRIMARY_BLUE}
                  title={t('settings.mcp_api.regenerate')}
                  disabled={!hasWriteAccess || busy}
                  onClick={() => setShowRegenerateConfirm(true)}
                />
                <Button
                  tone={ButtonToneProps.DESTRUCTIVE}
                  title={t('settings.mcp_api.revoke')}
                  disabled={!hasWriteAccess || busy}
                  onClick={() => setShowRevokeConfirm(true)}
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* Regenerate confirmation */}
      <Modal isOpen={showRegenerateConfirm} onClose={() => setShowRegenerateConfirm(false)}>
        <div className="max-w-460">
          <div className="border-subtle-bottom p-32">
            <h4 className="mb-12 typo-title-24">{t('settings.mcp_api.regenerate_confirm_title')}</h4>
            <p className="typo-text-16 text-faded">{t('settings.mcp_api.regenerate_confirm_text')}</p>
          </div>
          <div className="border-subtle-bottom py-16 px-24 flex justify-end">
            <Button
              className="mr-12"
              onClick={() => setShowRegenerateConfirm(false)}
              tone={ButtonToneProps.SECONDARY}
              title={t('general.cancel')}
            />
            <Button onClick={handleGenerate} tone={ButtonToneProps.PRIMARY_BLUE} title={t('general.confirm')} />
          </div>
        </div>
      </Modal>

      {/* Revoke confirmation */}
      <Modal isOpen={showRevokeConfirm} onClose={() => setShowRevokeConfirm(false)}>
        <div className="max-w-460">
          <div className="border-subtle-bottom p-32">
            <h4 className="mb-12 typo-title-24">{t('settings.mcp_api.revoke_confirm_title')}</h4>
            <p className="typo-text-16 text-faded">{t('settings.mcp_api.revoke_confirm_text')}</p>
          </div>
          <div className="border-subtle-bottom py-16 px-24 flex justify-end">
            <Button
              className="mr-12"
              onClick={() => setShowRevokeConfirm(false)}
              tone={ButtonToneProps.SECONDARY}
              title={t('general.cancel')}
            />
            <Button onClick={handleRevoke} tone={ButtonToneProps.DESTRUCTIVE} title={t('general.confirm')} />
          </div>
        </div>
      </Modal>

      {/* Key reveal — copy only, the key is never displayed */}
      <Modal isOpen={generatedKey !== null} onClose={() => setGeneratedKey(null)}>
        <div className="max-w-460">
          <div className="border-subtle-bottom p-32">
            <h4 className="mb-12 typo-title-24">{t('settings.mcp_api.key_generated_title')}</h4>
            <p className="typo-text-16 text-faded">{t('settings.mcp_api.key_generated_text')}</p>
          </div>
          <div className="border-subtle-bottom py-16 px-24 flex justify-end">
            <Button
              className="mr-12"
              onClick={handleCopy}
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('settings.mcp_api.copy_key')}
            />
            <Button
              onClick={() => setGeneratedKey(null)}
              tone={ButtonToneProps.SECONDARY}
              title={t('settings.mcp_api.done')}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default McpApiKeySettings;
